(ns app.front.view.group.admin.templates.stamps.builder
  "Editable stamp form. The caller owns the draft, image URLs and persistence callbacks."
  (:require [app.domain.stamp.model :as stamp-model]
            [app.front.file.upload :as upload]
            [app.front.stamp.render :as render]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.group.admin.templates.stamps.canvas :as canvas]
            [clojure.string :as str]
            [core.front.date-util :as date-util]
            [core.front.icons :as icons]
            [reagent.core :as r]
            [schema.core :as s]))

(defn persisted-state [{:keys [name assets layout]}]
  {:name   (str/trim (or name ""))
   :assets (select-keys assets (stamp-model/image-keys layout))
   :layout (assoc (render/measured-layout layout) :border-width (stamp-model/border-width layout))})

(defn dirty? [{:keys [draft baseline]}]
  (boolean (and draft baseline (not= baseline (persisted-state draft)))))

(defn reorder-element [elements id offset]
  (let [i (first (keep-indexed #(when (= id (:id %2)) %1) elements))
        j (when i (+ i offset))]
    (if (and j (<= 0 j) (< j (count elements)))
      (assoc elements i (nth elements j) j (nth elements i))
      elements)))

(defn add-image [draft {:keys [image-id image-key]} width height]
  (let [id    (str (random-uuid))
        scale (max (/ 144 (max width height)) (/ 0.72 (min width height)))]
    (-> draft
        (assoc-in [:assets image-key] image-id)
        (update-in [:layout :elements] conj
                   (assoc (stamp-model/new-element :image id) :asset image-key
                          :width (* width scale) :height (* height scale))))))

(defn format-number [value]
  (futil/to-fixed value 2))

(defn- numeric-field [{:keys [id label value on-change disabled?]}]
  (r/with-let [*text       (r/atom (format-number value))
               *last-value (atom value)]
    ;; Canvas gestures, reset actions and selection changes update props without firing this input's on-change.
    (when (not= @*last-value value)
      (reset! *last-value value)
      (reset! *text (format-number value)))
    [ui/number-input
     {:id         id
      :label-text label
      :value      @*text
      :step       0.01
      :disabled   disabled?
      :on-blur    #(reset! *text (format-number value))
      :on-change
      (fn [event]
        (let [raw    (futil/tval event)
              number (futil/parse-float raw)]
          ;; Preserve incomplete input, including the minus sign of off-canvas positions.
          (when (re-matches #"-?\d*(\.\d{0,2})?" raw)
            (reset! *text raw)
            (when (and number
                       (not= number (js/Number (format-number value))))
              (reset! *last-value number)
              (on-change number)))))}]))

(defn- color-field [id label color disabled? on-change]
  [:label.flex.items-center.gap-3.text-sm
   {:for id}
   label
   [:input
    {:id        id
     :type      "color"
     :value     color
     :disabled  disabled?
     :on-change #(on-change (futil/tval %))}]])

(defn- font-sizes [current saved]
  ;; Retain custom saved sizes exactly; opening the editor never rounds stored geometry.
  (sort (conj #{8 9 10 11 12 14 16 18 20 24 28 32 36 48 60 72} current (or saved current))))

(defn editor
  [{:keys [tr draft dirty? context image-urls upload-component-id busy? error on-change on-upload on-save on-cancel]
    :or   {busy? false}}]
  (r/with-let [*selected          (r/atom nil)
               opening-font-sizes (into {} (map (juxt :id :font-size)) (get-in draft [:layout :elements]))
               *opening-elements  (r/atom (mapv render/measured-element (get-in draft [:layout :elements])))]
    (let [stamp-name       (:name draft)
          layout           (render/measured-layout (:layout draft))
          elements         (:elements layout)
          selected-element (some #(when (= @*selected (:id %)) %) elements)
          change-layout!   #(on-change (assoc draft :layout %))
          change-element!
          (fn [element]
            (let [updated  (update layout :elements
                                   (fn [elements]
                                     (mapv #(if (= (:id %) (:id element)) (render/measured-element element) %)
                                           elements)))
                  previous (some #(when (= (:id %) (:id element)) %) elements)]
              ;; Recover newly grown text overflow on content edits, never during a gesture.
              (when (and (#{:text :date} (:type element))
                         (not= (select-keys previous [:text :font-size])
                               (select-keys element [:text :font-size])))
                (reset! *opening-elements (:elements updated)))
              (change-layout! updated)))
          valid?           (and (nil? (s/check stamp-model/StampName (str/trim (or stamp-name ""))))
                                (stamp-model/valid-layout? layout))
          viewport         (canvas/editing-viewport (assoc layout :elements @*opening-elements))
          snapshot         (merge context {:layout     layout
                                           :image-urls image-urls
                                           :date       (.format date-util/calendar-date-format (js/Date.))})]
      [:div.space-y-4.min-w-0
       {:data-guide-page-description "Design an authority group stamp on a transparent physical-size canvas."
        :class                       "[&_.cloudpermit--number-input_.input-unit]:hidden"}
       [:div.border.border-solid.border-gray-300.rounded.p-4.space-y-3
        {:role            "region"
         :aria-labelledby "stamp-settings"}
        [:h3.text-sm.font-bold
         {:id "stamp-settings"}
         (tr [:stamp/settings])]
        [:div.grid.gap-4.items-end
         {:class "grid-cols-[repeat(auto-fit,minmax(min(100%,20rem),1fr))]"}
         [:div.min-w-0
          {:data-guide-id    "stamp-name"
           :data-guide-label "Stamp name"}
          [ui/text-input
           {:id         "stamp-name"
            :label-text (tr [:stamp/name])
            :value      stamp-name
            :required   true
            :disabled   busy?
            :on-change  #(on-change (assoc draft :name (futil/tval %)))}]]
         [:div.grid.grid-cols-2.gap-3
          (futil/for-frag [key [:width :height]]
            [numeric-field
             {:id        (str "canvas-" (name key))
              :label     (tr [(keyword "stamp" (str "canvas-" (name key)))])
              :value     (/ (key layout) stamp-model/points-per-inch)
              :disabled? busy?
              :on-change #(change-layout! (assoc layout key (* stamp-model/points-per-inch %)))}])]
         [:div.flex.flex-wrap.items-center.gap-3
          {:class   "col-span-full"
           :data-cy "stamp-border-controls"}
          [ui/button
           {:kind     :tertiary
            :size     :small
            :disabled busy?
            :on-click #(change-layout! (if (:border layout)
                                         (dissoc layout :border)
                                         (assoc layout :border "#111111")))}
           (tr [(if (:border layout) :stamp/remove-border :stamp/add-border)])]
          (when (:border layout)
            [:<>
             [color-field "canvas-border" (tr [:stamp/border-color]) (:border layout) busy?
              #(change-layout! (assoc layout :border %))]
             [:div.min-w-0
              {:class "w-40 max-w-full"}
              [numeric-field
               {:id        "canvas-border-width"
                :label     (tr [:stamp/border-width])
                :value     (stamp-model/border-width layout)
                :disabled? busy?
                :on-change #(change-layout! (assoc layout :border-width %))}]]])]]]
       [:div.flex.flex-wrap.gap-4.items-start
        [canvas/stamp-canvas
         {:tr          tr
          :snapshot    snapshot
          :viewport    viewport
          :selected-id @*selected
          :disabled?   busy?
          :on-select   #(reset! *selected %)
          :on-change   change-element!
          :toolbar
          [:div.flex.flex-wrap.justify-center.gap-1.bg-white.border.border-solid.border-gray-300.rounded.shadow-sm.p-1
           {:role       "toolbar"
            :aria-label (tr [:stamp/tools])}
           (futil/for-frag [type [:text :image :signature :date]]
             [:div
              {:data-guide-id    (str "stamp-" (name type))
               :data-guide-label (str "Add stamp " (name type))}
              (if (= type :image)
                [upload/file-picker-button
                 {:id               upload-component-id
                  :tr               tr
                  :class            "[&_.bx--inline-loading]:!h-8"
                  :accept-types     stamp-model/image-content-types
                  :size-constraints {:bytes  stamp-model/max-image-bytes
                                     :width  stamp-model/max-image-dimension
                                     :height stamp-model/max-image-dimension}
                  :button-opts      {:kind     :ghost
                                     :size     :small
                                     :disabled (or busy? (>= (count elements) stamp-model/max-elements))}
                  :on-select-fn     on-upload}
                 (tr [:stamp/image])]
                [ui/button
                 {:kind     :tertiary
                  :size     :small
                  :disabled (or busy? (>= (count elements) stamp-model/max-elements))
                  :on-click #(let [id (str (random-uuid))]
                               (reset! *selected id)
                               (change-layout! (update layout :elements conj
                                                       (stamp-model/new-element type id))))}
                 (tr [(keyword "stamp" (name type))])])])]}]
        [:aside.min-w-0.border.border-solid.border-gray-300.rounded.p-4.space-y-4
         {:aria-label (tr [:stamp/element])
          :class      "flex-[1_1_20rem]"}
         [ui-select/select
          {:id         "stamp-element"
           :label-text (tr [:stamp/element])
           :values     (mapv :id elements)
           :value      @*selected
           :on-change  #(reset! *selected %)
           :val->str   (fn [id]
                         (let [i (first (keep-indexed #(when (= id (:id %2)) %1) elements))]
                           (str (inc i) ". "
                                (tr [(keyword "stamp" (name (:type (nth elements i))))]))))}]
         (when selected-element
           [:fieldset.grid.grid-cols-2.gap-3.min-w-0
            {:disabled   busy?
             :aria-label (tr [:stamp/properties])}
            [:div.col-span-2.flex.flex-wrap.gap-2
             (futil/for-frag [[offset label extreme] [[1 :stamp/bring-forward (last elements)]
                                                      [-1 :stamp/send-backward (first elements)]]]
               [ui/button
                {:kind     :tertiary
                 :size     :small
                 :disabled (or busy? (= (:id selected-element) (:id extreme)))
                 :on-click #(change-layout! (update layout :elements reorder-element @*selected offset))}
                (tr [label])])]
            (when (= :text (:type selected-element))
              [:div.col-span-2
               [ui/text-area
                {:id         "stamp-text"
                 :label-text (tr [:stamp/text-content])
                 :value      (:text selected-element)
                 :on-change  #(change-element! (assoc selected-element :text (futil/tval %)))}]])
            (futil/for-frag [[key label] [[:x :stamp/x] [:y :stamp/y]
                                          [:width :stamp/element-width] [:height :stamp/element-height]]]
              [numeric-field
               {:id        (str "element-" (name key))
                :label     (tr [label])
                :value     (/ (key selected-element) stamp-model/points-per-inch)
                :disabled? (or busy? (and (#{:text :date} (:type selected-element)) (#{:width :height} key)))
                :on-change
                #(when (or (not (#{:width :height} key)) (pos? %))
                   (change-element!
                     (cond-> (assoc selected-element key (* stamp-model/points-per-inch %))
                       (and (= :image (:type selected-element)) (#{:width :height} key))
                       (assoc (if (= key :width) :height :width)
                              (* (if (= key :width) (:height selected-element) (:width selected-element))
                                 (/ (* stamp-model/points-per-inch %) (key selected-element)))))))}])
            [:div.col-span-2.flex.items-end.gap-2
             [:div.flex-1.min-w-0
              [numeric-field
               {:id        "element-rotation"
                :label     (tr [:stamp/rotation])
                :value     (:rotation selected-element)
                :disabled? busy?
                :on-change #(change-element! (assoc selected-element :rotation %))}]]
             [ui/button
              {:kind     :tertiary
               :size     :small
               :icon     icons/rotate-left
               :disabled (or busy? (zero? (:rotation selected-element)))
               :on-click #(change-element! (assoc selected-element :rotation 0))}
              (tr [:stamp/reset-rotation])]]
            (when (#{:text :date} (:type selected-element))
              [:div.col-span-2
               [ui-select/select
                {:id           "stamp-font-size"
                 :label-text   (tr [:stamp/font-size])
                 :values       (font-sizes (:font-size selected-element) (get opening-font-sizes @*selected))
                 :value        (:font-size selected-element)
                 :val->str     str
                 :allow-empty? false
                 :on-change    #(change-element! (assoc selected-element :font-size %))}]])
            (when (not= :image (:type selected-element))
              [:div.col-span-2.py-2
               [color-field "stamp-color" (tr [:stamp/text-color]) (:color selected-element) busy?
                #(change-element! (assoc selected-element :color %))]])
            [:div.col-span-2.border-t.border-gray-200.pt-3
             [ui/button
              {:kind     :danger
               :size     :small
               :disabled busy?
               :on-click #(do (change-layout! (update layout :elements
                                                      (fn [xs] (filterv (fn [x] (not= @*selected (:id x))) xs))))
                              (reset! *selected nil))}
              (tr [:delete])]]])]]
       (when-not (stamp-model/valid-layout? layout)
         [:p {:role "alert"}
          (tr [:stamp/invalid-layout])])
       (when error
         [:p {:role "alert"}
          (tr [error])])
       [:div.flex.gap-3.border-t.border-gray-200.pt-4
        (when dirty?
          [ui/button
           {:disabled (or busy? (not valid?))
            :on-click on-save}
           (tr [:save])])
        [ui/button
         {:kind     :secondary
          :disabled busy?
          :on-click on-cancel}
         (tr [:cancel])]]])))
